import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Operator guardrail rules (al-tutor migration 107).
 *
 * Two scopes, one endpoint family:
 *   - organisation-wide rules  → `/guardrails/rules`
 *   - bot-specific rules       → `/tutor/{id}/guardrails/rules`
 *
 * A bot listing returns the inherited organisation rules alongside the bot's
 * own; `bot_id === null` identifies the inherited ones, which are read-only
 * on the tutor page (a bot rule can never disable an organisation rule).
 */

export type GuardrailEnforce = 'hard' | 'advisory';
export type GuardrailAction = 'block' | 'redirect' | 'log_only';
export type GuardrailSeverity = 'low' | 'medium' | 'high';

export interface GuardrailRule {
  id: string;
  client_id: string;
  /** null = organisation-wide (inherited by every bot of this client) */
  bot_id: string | null;
  name: string;
  definition: string;
  examples: string[];
  enforce: GuardrailEnforce;
  action: GuardrailAction;
  redirect_hint?: string | null;
  severity: GuardrailSeverity;
  /** null = every template */
  applies_to_templates?: string[] | null;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
  /** Trigger count over the requested window, filled by the backend. */
  hit_count: number;
}

export interface GuardrailRuleInput {
  name: string;
  definition: string;
  examples: string[];
  enforce: GuardrailEnforce;
  action: GuardrailAction;
  redirect_hint?: string | null;
  severity: GuardrailSeverity;
  applies_to_templates?: string[] | null;
  enabled: boolean;
}

export interface GuardrailHit {
  id: number;
  rule_id: string;
  bot_id?: string | null;
  user_id?: string | null;
  thread_id?: string | null;
  matched_text?: string | null;
  action_taken: string;
  confidence?: number | null;
  created_at?: string;
}

/** Backend list envelope. `total` is the unfiltered row count, which can
 *  exceed `data.length` when the fetch limit bites — the page surfaces that
 *  instead of silently showing a short list. */
export interface GuardrailPage<T> {
  data: T[];
  total: number;
}

// Endpoint maxima. Paging itself happens client-side, like every other table
// in the portal (faqs, curriculum, placements) — these are the ceilings for
// the single fetch behind it.
const RULES_FETCH_LIMIT = 500;
const HITS_FETCH_LIMIT = 200;

@Injectable({
  providedIn: 'root',
})
export class GuardrailService {
  private baseUrl = `${environment.tutorsBackend}`;

  constructor(private http: HttpClient) {}

  listClientRules(hitWindowDays = 30) {
    const params = new HttpParams()
      .set('hit_window_days', hitWindowDays)
      .set('limit', RULES_FETCH_LIMIT);
    return this.http
      .get(`${this.baseUrl}/guardrails/rules`, { params })
      .pipe(map((d: any) => d as GuardrailPage<GuardrailRule>));
  }

  listBotRules(tutorId: string, includeInherited = true, hitWindowDays = 30) {
    const params = new HttpParams()
      .set('include_inherited', includeInherited)
      .set('hit_window_days', hitWindowDays)
      .set('limit', RULES_FETCH_LIMIT);
    return this.http
      .get(`${this.baseUrl}/tutor/${tutorId}/guardrails/rules`, { params })
      .pipe(map((d: any) => d as GuardrailPage<GuardrailRule>));
  }

  createClientRule(input: GuardrailRuleInput) {
    return this.http
      .post(`${this.baseUrl}/guardrails/rules`, input)
      .pipe(map((r: any) => r as GuardrailRule));
  }

  createBotRule(tutorId: string, input: GuardrailRuleInput) {
    return this.http
      .post(`${this.baseUrl}/tutor/${tutorId}/guardrails/rules`, input)
      .pipe(map((r: any) => r as GuardrailRule));
  }

  updateRule(ruleId: string, input: Partial<GuardrailRuleInput>) {
    return this.http
      .patch(`${this.baseUrl}/guardrails/rules/${ruleId}`, input)
      .pipe(map((r: any) => r as GuardrailRule));
  }

  deleteRule(ruleId: string) {
    return this.http.delete(`${this.baseUrl}/guardrails/rules/${ruleId}`);
  }

  listHits(ruleId: string, limit = HITS_FETCH_LIMIT, offset = 0) {
    const params = new HttpParams().set('limit', limit).set('offset', offset);
    return this.http
      .get(`${this.baseUrl}/guardrails/rules/${ruleId}/hits`, { params })
      .pipe(map((d: any) => d as GuardrailPage<GuardrailHit>));
  }

  /** POST, not DELETE — the id list travels in the body, and a DELETE with a
   *  payload is inconsistently supported across proxies. */
  deleteHits(ruleId: string, ids: number[]) {
    return this.http.post(
      `${this.baseUrl}/guardrails/rules/${ruleId}/hits/delete`,
      { ids }
    );
  }
}
